import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../../../services/api';

// Employee self-service preboarding ("İşe Hazırlığım").
// Mirrors the mobile preboarding screens against the /v1/my-preboarding API.
//
// Load order: GetHome returns { Status:true, Data:null } when the user has no
// active preboarding (legitimate empty state, no toast). The sub-endpoints
// (tasks/documents/welcome-content) only 404 when there is NO active
// preboarding, so we gate their fetch on home confirming one — when a
// preboarding exists but has zero items they return an empty list, no toast.
export default function useMyPreboardingApi() {
  const [loading, setLoading] = useState(true);
  const [home, setHome] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [welcomeContent, setWelcomeContent] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      const homeRes = await api.get('/v1/my-preboarding');
      const homeData = homeRes?.data?.data || null;
      if (cancelled) return;

      setHome(homeData);

      if (!homeData) {
        setTasks([]);
        setDocuments([]);
        setWelcomeContent([]);
        setLoading(false);
        return;
      }

      const [tasksRes, docsRes, welcomeRes] = await Promise.all([
        api.get('/v1/my-preboarding/tasks'),
        api.get('/v1/my-preboarding/documents'),
        api.get('/v1/my-preboarding/welcome-content'),
      ]);
      if (cancelled) return;

      setTasks(unwrapList(tasksRes));
      setDocuments(unwrapList(docsRes));
      setWelcomeContent(unwrapList(welcomeRes));
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [refreshTrigger]);

  const startTask = useCallback(async (taskId) => {
    try {
      await api.post(`/v1/my-preboarding/tasks/${taskId}/start`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const completeTask = useCallback(async (taskId, notesMarkdown) => {
    try {
      await api.post(`/v1/my-preboarding/tasks/${taskId}/complete`, { notesMarkdown: notesMarkdown || null });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // Upload a chosen File for a document requirement: presign → PUT to R2 → record url.
  // Mirrors GenericFormDrawer.uploadFiles (presigned-URL flow).
  // OB-F — `kvkkConsentGranted` SUNUCUYA TAŞINIR. Arayüz her belgeyi bir KVKK
  // kategorisiyle etiketliyordu ama hiçbir rıza kaydı üretilmiyordu; bayrak
  // gönderilmezse sunucu rıza YAZMAZ (eski istemcilerle geriye uyumluluk),
  // yani bu parametreyi düşürmek düzeltmeyi SESSİZCE öldürür.
  const uploadDocument = useCallback(async (documentId, file, consentVersion, kvkkConsentGranted) => {
    try {
      const presignRes = await api.post(
        `/v1/storage/presign?fileName=${encodeURIComponent(file.name)}&mimeType=${encodeURIComponent(file.type)}&purpose=${encodeURIComponent('preboardingDocument')}`,
      );
      const { upload_url: uploadUrl, file_url: fileUrl } = presignRes.data || {};
      if (!uploadUrl || !fileUrl) return false;

      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });

      await api.post(`/v1/my-preboarding/documents/${documentId}/upload`, {
        fileUrl,
        fileId: null,
        consentVersion: consentVersion || null,
        kvkkConsentGranted: Boolean(kvkkConsentGranted),
      });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const markContentViewed = useCallback(async (contentId) => {
    try {
      await api.post(`/v1/my-preboarding/welcome-content/${contentId}/mark-viewed`);
      // Optimistic: update locally so the checkmark appears without a full reload.
      setWelcomeContent((prev) => prev.map((c) => (c.id === contentId
        ? { ...c, viewedAt: c.viewedAt || new Date().toISOString() }
        : c)));
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    loading,
    home,
    tasks,
    documents,
    welcomeContent,
    refresh,
    startTask,
    completeTask,
    uploadDocument,
    markContentViewed,
  };
}

// Sub-endpoints return PaginatedList<T> wrapped in ResponseObjectModel.Data:
// { data: { data: [...], totalRowCount, pageIndex, pageSize } }.
function unwrapList(res) {
  const payload = res?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}
